import { FixedStepDriver } from "../../core/timing.ts";
import {
  EMPTY_HELD_INPUT,
  parseNormalizedHeldInput,
  parseNormalizedLifecycleEvent,
  type NormalizedHeldInput,
} from "../../core/lifecycle/events.ts";
import { createManualChainSimulation } from "./simulation.ts";
import type {
  ManualChainRuntime,
  ManualChainRuntimeOptions,
  ManualChainS04Port,
  ManualChainTickCommand,
} from "./types.ts";

const EMPTY_COMMAND: Readonly<ManualChainTickCommand> = Object.freeze({
  interaction: null,
  routeTarget: null,
  explicitCancel: false,
  actorPresent: true,
  bodyVisible: true,
});

function parseCommand(value: unknown): Readonly<ManualChainTickCommand> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new TypeError("manual-chain command must be a record");
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort();
  if (keys.join(",") !== "actorPresent,bodyVisible,explicitCancel,interaction,routeTarget") throw new TypeError("manual-chain command keys are invalid");
  const interactions = [null, "harvest", "stage", "tend", "collect", "sale"];
  const routeTargets = [null, "pen", "market"];
  if (!interactions.includes(record.interaction as never) || !routeTargets.includes(record.routeTarget as never)) throw new TypeError("manual-chain command value is invalid");
  if (typeof record.explicitCancel !== "boolean" || typeof record.actorPresent !== "boolean" || typeof record.bodyVisible !== "boolean") throw new TypeError("manual-chain command flags are invalid");
  return Object.freeze({
    interaction: record.interaction as ManualChainTickCommand["interaction"],
    routeTarget: record.routeTarget as ManualChainTickCommand["routeTarget"],
    explicitCancel: record.explicitCancel,
    actorPresent: record.actorPresent,
    bodyVisible: record.bodyVisible,
  });
}

export const TEST_ONLY_ACCEPTING_S04_PORT: ManualChainS04Port = Object.freeze({
  commit: (request: Parameters<ManualChainS04Port["commit"]>[0]) => Object.freeze({ accepted: true, lifecycleSlice: request.lifecycleSlice }),
});

export function createManualChainRuntime(options: Readonly<ManualChainRuntimeOptions>): ManualChainRuntime {
  const simulation = createManualChainSimulation(options.s04);
  let command = EMPTY_COMMAND;
  let input: Readonly<NormalizedHeldInput> = EMPTY_HELD_INPUT;
  let pendingFrameAlignmentOriginMs: number | null = options.monotonicOriginMs;
  const driver = new FixedStepDriver({
    initialSnapshot: simulation.initialSnapshot,
    monotonicOriginMs: options.monotonicOriginMs,
    tick: (snapshot, context) => simulation.tick(snapshot, {
      tick: context.tick,
      tickMs: context.tickMs,
      heldInput: input,
      command,
    }),
  });
  return Object.freeze({
    get snapshot() { return driver.currentSnapshot; },
    submitHeldInput: (value: unknown) => {
      const parsed = parseNormalizedHeldInput(value);
      const accepted = driver.submitHeldInput(parsed);
      if (accepted) input = parsed;
      return accepted;
    },
    issueCommand: (value: unknown) => {
      if (driver.isPaused) return false;
      command = parseCommand(value);
      return true;
    },
    advanceFrame: (monotonicNowMs: number) => {
      if (
        pendingFrameAlignmentOriginMs !== null &&
        Number.isFinite(monotonicNowMs) &&
        monotonicNowMs >= 0 &&
        monotonicNowMs < pendingFrameAlignmentOriginMs
      ) {
        driver.handleLifecycle({ type: "resume", monotonicOriginMs: monotonicNowMs });
      }
      pendingFrameAlignmentOriginMs = null;
      return driver.advanceFrame(monotonicNowMs);
    },
    handleLifecycle: (value: unknown) => {
      const event = parseNormalizedLifecycleEvent(value);
      driver.handleLifecycle(event);
      pendingFrameAlignmentOriginMs = event.type === "resume" ? event.monotonicOriginMs : null;
      input = EMPTY_HELD_INPUT;
      command = EMPTY_COMMAND;
    },
    drainEvents: (afterSequence = 0) => driver.currentSnapshot.state.events.filter((event) => event.sequence > afterSequence),
  });
}
